import clsx from 'clsx';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

export type ButtonVariant =
  'default' | 'primary' | 'secondary' | 'ghost' | 'danger' | 'danger-solid' | 'accent';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * default = secondary (white, outlined). accent = lime, broadcast/public surfaces only.
   * danger = red outline (destructive actions on a page: never the loudest thing on screen);
   * danger-solid = filled red, only for the confirm button inside a confirmation dialog.
   * Disabled buttons of every variant share one neutral look (no lime line, readable label).
   */
  variant?: ButtonVariant;
  /** sm = 36px (dense console toolbars, never on touch-first screens), md = --touch-min, lg = --touch-xl */
  size?: ButtonSize;
  block?: boolean;
  /** leading icon (e.g. a lucide-react icon at size 18) */
  icon?: ReactNode;
  /** trailing icon */
  iconEnd?: ReactNode;
  loading?: boolean;
}

/**
 * Class names of a button, for elements that must stay links (TanStack `<Link className={…}>`,
 * `<a href download>`). Buttons never show an underline.
 */
export function buttonClass({
  variant = 'default',
  size = 'md',
  block,
  iconOnly,
}: { variant?: ButtonVariant; size?: ButtonSize; block?: boolean; iconOnly?: boolean } = {}) {
  return clsx(
    'ui-btn',
    variant !== 'default' && variant !== 'secondary' && `ui-btn--${variant}`,
    size !== 'md' && `ui-btn--${size}`,
    block && 'ui-btn--block',
    iconOnly && 'ui-btn--icon',
  );
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'default',
    size = 'md',
    block,
    icon,
    iconEnd,
    loading,
    className,
    type = 'button',
    disabled,
    children,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={clsx(buttonClass({ variant, size, block }), className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <span className="ui-btn__spinner" aria-hidden="true" /> : icon}
      {children}
      {iconEnd}
    </button>
  );
});

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** accessible name (required: the button shows only an icon) */
  label: string;
  icon: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
}

/** Square icon-only button; `label` becomes aria-label and the tooltip. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, icon, variant = 'ghost', size = 'md', className, type = 'button', title, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={title ?? label}
      className={clsx(buttonClass({ variant, size, iconOnly: true }), className)}
      {...rest}
    >
      {icon}
    </button>
  );
});
